/**
 * Settings Layout
 *
 * Sidebar-nav modal on desktop, full-screen pages on mobile.
 * Active tab is driven by `?tab=` URL search param for deep-linking.
 */

import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  User,
  Mail,
  Bot,
  Database,
  Puzzle,
  Code,
  ArrowLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { AccountTab } from './tabs/AccountTab';
import { DigestsEmailTab } from './tabs/DigestsEmailTab';
import { AiAutomationTab } from './tabs/AiAutomationTab';
import { DataSyncTab } from './tabs/DataSyncTab';
import { IntegrationsTab } from './tabs/IntegrationsTab';
import { DeveloperTab } from './tabs/DeveloperTab';

export type TabId =
  | 'account'
  | 'digests-email'
  | 'ai-automation'
  | 'data-sync'
  | 'integrations'
  | 'developer';

interface TabDef {
  id: TabId;
  label: string;
  icon: typeof User;
  description: string;
}

const TABS: TabDef[] = [
  { id: 'account', label: 'Account', icon: User, description: 'Profile, appearance, delete account' },
  { id: 'digests-email', label: 'Digests & Email', icon: Mail, description: 'Morning digest, email capture, notifications' },
  { id: 'ai-automation', label: 'AI & Automation', icon: Bot, description: 'AI model, cost settings, classification' },
  { id: 'data-sync', label: 'Data & Sync', icon: Database, description: 'Sync mode, views, maintenance' },
  { id: 'integrations', label: 'Integrations', icon: Puzzle, description: 'Google Calendar, Gmail' },
  { id: 'developer', label: 'Developer', icon: Code, description: 'API URL, dev mode, API keys' },
];

const TAB_COMPONENTS: Record<TabId, React.FC> = {
  account: AccountTab,
  'digests-email': DigestsEmailTab,
  'ai-automation': AiAutomationTab,
  'data-sync': DataSyncTab,
  integrations: IntegrationsTab,
  developer: DeveloperTab,
};

interface SettingsLayoutProps {
  onClose: () => void;
}

export function SettingsLayout({ onClose }: SettingsLayoutProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const activeTab = (searchParams.get('tab') as TabId | null) ?? (isMobile ? null : 'account');

  const setActiveTab = useCallback(
    (tabId: TabId | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tabId) {
            next.set('tab', tabId);
          } else {
            next.delete('tab');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Keyboard navigation: arrow keys cycle tabs on desktop
  useEffect(() => {
    if (isMobile) return;

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const currentIndex = TABS.findIndex((t) => t.id === activeTab);
        const nextIndex =
          e.key === 'ArrowDown'
            ? (currentIndex + 1) % TABS.length
            : (currentIndex - 1 + TABS.length) % TABS.length;
        setActiveTab(TABS[nextIndex].id);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isMobile, activeTab, setActiveTab]);

  // Clean up ?tab param on unmount
  useEffect(() => {
    return () => {
      const params = new URLSearchParams(window.location.search);
      if (params.has('tab')) {
        params.delete('tab');
        const nextUrl = params.toString()
          ? `${window.location.pathname}?${params.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, '', nextUrl);
      }
    };
  }, []);

  const ActiveComponent = activeTab ? TAB_COMPONENTS[activeTab] : null;
  const activeTabDef = activeTab ? TABS.find((t) => t.id === activeTab) : null;

  // ── Mobile layout ──
  if (isMobile) {
    // If a tab is selected, show it full-screen with back button
    if (activeTab && ActiveComponent) {
      return (
        <div className="h-full flex flex-col overflow-hidden bg-surface-base-light dark:bg-surface-base-dark">
          {/* Mobile tab header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle-light dark:border-border-subtle-dark bg-surface-raised-light dark:bg-surface-raised-dark">
            <button
              onClick={() => setActiveTab(null)}
              className="p-1.5 -ml-1.5 rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
            </button>
            <h1 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
              {activeTabDef?.label}
            </h1>
          </div>
          <div className="flex-1 overflow-y-auto pb-safe">
            <div className="max-w-2xl mx-auto p-4 space-y-6">
              <ActiveComponent />
            </div>
          </div>
        </div>
      );
    }

    // Tab index (list of rows)
    return (
      <div className="h-full flex flex-col overflow-hidden bg-surface-base-light dark:bg-surface-base-dark">
        {/* Mobile header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle-light dark:border-border-subtle-dark bg-surface-raised-light dark:bg-surface-raised-dark">
          <div className="flex items-center gap-3">
            <h1 className="text-sm font-semibold uppercase tracking-widest text-text-tertiary-light dark:text-text-tertiary-dark">
              Settings
            </h1>
          </div>
          <button
            onClick={onClose}
            aria-label="Close settings"
            className="p-1.5 text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto pb-safe">
          <div className="divide-y divide-border-subtle-light dark:divide-border-subtle-dark">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="w-full flex items-center gap-4 px-4 py-4 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors text-left"
                >
                  <div className="p-2 bg-surface-hover-light dark:bg-surface-hover-dark rounded-lg shrink-0">
                    <Icon className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                      {tab.label}
                    </p>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
                      {tab.description}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-text-tertiary-light dark:text-text-tertiary-dark shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Desktop layout: sidebar + content ──
  return (
    <div className="h-full flex overflow-hidden relative">
      {/* Close button — top-right corner, detached from sidebar */}
      <button
        onClick={onClose}
        aria-label="Close settings"
        className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Sidebar — narrower, quieter background, no heavy header */}
      <div className="w-44 shrink-0 border-r border-border-subtle-light dark:border-border-subtle-dark bg-surface-base-light dark:bg-[#0e1420] flex flex-col">
        {/* Label — muted, not a page title */}
        <div className="px-4 pt-5 pb-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-tertiary-light dark:text-text-tertiary-dark">
            Settings
          </span>
        </div>

        {/* Nav items — tighter, left-bar active indicator */}
        <nav className="flex-1 px-1.5 pb-3 space-y-px overflow-y-auto" role="tablist" aria-orientation="vertical">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={`relative w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                  isActive
                    ? 'font-medium text-text-primary-light dark:text-text-primary-dark bg-surface-hover-light dark:bg-surface-hover-dark'
                    : 'font-normal text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light/50 dark:hover:bg-surface-hover-dark/50'
                }`}
              >
                {/* Left accent bar for active state */}
                {isActive && (
                  <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full bg-primary-500 dark:bg-primary-400" />
                )}
                <Icon
                  className={`w-3.5 h-3.5 shrink-0 ${
                    isActive
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-text-tertiary-light dark:text-text-tertiary-dark'
                  }`}
                />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto" role="tabpanel">
        <div className="max-w-2xl mx-auto px-6 pt-5 pb-8 space-y-6">
          {ActiveComponent && <ActiveComponent />}
        </div>
      </div>
    </div>
  );
}
